import { useState, useEffect, useRef, useCallback } from "react";
import { useParams } from "react-router-dom";
import config from "../../config";
import { useLanguage } from "../../Context";

export default function BookSubcat() {
  const { subcategory } = useParams();
  const { language } = useLanguage();

  const [contents, setContents] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const loaderRef = useRef(null);

  // Fetch books for subcategory
  const fetchBooks = useCallback(
    async (reset = false) => {
      if (!hasMore && !reset) return;
      if (reset) {
        setIsLoading(true);
        setPage(1);
      }
      try {
        const response = await fetch(
          `${
            config.apiUrl
          }lazyLoad.php?option=2&categoryid=5&subcategory=${subcategory}&page=${
            reset ? 1 : page
          }&lang=${language}`
        );
        const data = await response.json();
        const newContents = data.map((item) => ({
          id: item.id,
          categoryE: item.categoryE,
          FileUrl: item.FileUrl,
          ImageUrl: item.ImageUrl,
          PostDate: item.PostDate,
          ShortDesc: item.ShortDesc,
          SubCategory: item.SubCategory,
          SubCategoryd: item.SubCategoryd,
          Title: item.Title,
          contentViews: item.contentViews,
          fulldesc: item.fulldesc,
        }));

        if (reset) {
          setContents(newContents);
          setHasMore(newContents.length > 0);
          setPage(2);
        } else {
          setContents((prev) => [...prev, ...newContents]);
          setHasMore(newContents.length > 0);
          setPage((prev) => prev + 1);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
      // eslint-disable-next-line
    },
    [subcategory, language, page, hasMore]
  );

  // Reset and fetch on subcategory/language change
  useEffect(() => {
    setContents([]);
    setHasMore(true);
    setPage(1);
    fetchBooks(true);
    // eslint-disable-next-line
  }, [subcategory, language]);

  // Infinite scroll using IntersectionObserver
  useEffect(() => {
    if (!hasMore || isLoading) return;
    const observer = new window.IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !isLoading) {
          fetchBooks();
        }
      },
      { threshold: 1 }
    );
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => {
      if (loaderRef.current) observer.unobserve(loaderRef.current);
    };
    // eslint-disable-next-line
  }, [loaderRef, hasMore, isLoading, fetchBooks]);

  const styles = {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(15rem, 2fr))",
    gap: "1rem",
  };

  return (
    <div className="bg-body">
      <div className="max-container-width py-3 text-body">
        <div className="mb-2">
          <h3
            className="border-end border-5 pe-2 primary-text-blue"
            id="secondary-border-yellow"
          >
            {parseInt(language) === 1 ? "کتاب ها" : "کتابونه"}
          </h3>
        </div>
        <div style={styles}>
          {contents.map((book) => (
            <div
              key={book.id}
              className="card bg-body-secondary border-0 rounded-1 p-2"
            >
              <a
                href={`/book/${book.SubCategory}/${book.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={book.ImageUrl?.replace(
                    /_large(?=\.(jpg|jpeg|png)$)/,
                    "_thumb"
                  )}
                  className="card-img-top"
                  alt="thumbnail"
                  style={{ width: "100%", objectFit: "cover" }}
                />
              </a>
              <div className="card-body p-1 text-center">
                <h4 className="card-text fs-6 mb-1 p-1" id="publication-title">
                  {book.Title}
                </h4>
                <p className="text-break limit-text fs-6">{book.ShortDesc}</p>
                <a
                  href={`/book/${book.SubCategory}/${book.id}`}
                  rel="noreferrer"
                  target="_blank"
                  className="btn yellow-btn-hover px-4 text-dark secondary-bg-yellow border-0 rounded-1"
                >
                  مطالعه کتاب
                </a>
              </div>
            </div>
          ))}
        </div>
        {/* Loader for infinite scroll (always rendered for observer) */}
        <div
          ref={loaderRef}
          className="text-center mb-4"
          style={{ minHeight: 40 }}
        >
          {isLoading && (
            <div className="placeholder-glow mb-4" style={styles}>
              {[...Array(5)].map((_, index) => (
                <div key={index} className="bg-body-secondary">
                  <div
                    className="w-100 placeholder"
                    style={{ height: "18rem" }}
                  />
                  <div className="p-3 text-center">
                    <div className="col-12 placeholder mb-3" />
                    <div className="col-6 placeholder btn" />
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {!hasMore && !isLoading && (
          <div className="mt-5 text-center">
            <small className="p-2 bg-secondary text-light rounded">
              {parseInt(language) === 1 ? "پایان مطالب" : "د محتوا پای"}
            </small>
          </div>
        )}
      </div>
    </div>
  );
}
